import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { useEmploymentTypeOptions } from "@/hooks/use-employment-types";

/**
 * Tones for the seeded employment types. Only the COLOUR lives here — the label
 * comes from the admin-managed list (EPIC-028), so a rename in Settings shows
 * up on the badge. An admin-added type gets the neutral tone; adding a tone for
 * it is a deliberate code change, and a wrong colour is a far smaller problem
 * than a stale label.
 */
const employmentTypeTone: Record<string, StatusTone> = {
  full_time: "success",
  part_time: "info",
  contractor: "navy",
  variable: "warning",
};

const statusMap: Record<string, { tone: StatusTone; label: string }> = {
  active: { tone: "success", label: "Active" },
  inactive: { tone: "neutral", label: "Inactive" },
};

export function PersonEmploymentTypeBadge({ employmentType }: { employmentType: string }) {
  // `labelFor` falls back to the raw token while the list is still loading, or
  // if the type has been deleted out from under the row — the same posture as
  // the audit read path, which prefers a readable token over "(deleted)".
  const label = useEmploymentTypeOptions().labelFor(employmentType);
  return <StatusPill tone={employmentTypeTone[employmentType] ?? "neutral"} label={label} />;
}

export function PersonStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const mapped = statusMap[status] ?? { tone: "neutral" as StatusTone, label: status };
  return <StatusPill tone={mapped.tone} label={mapped.label} />;
}
